import React, { useEffect, useState } from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface Chatbot {
  id: string;
  public_id: string;
  name: string;
  is_default: boolean | null;
}

export function ChatbotSelector() {
  const { user } = useAuth();
  const { chatbotPublicId, refetchChatbot } = useChatbot();
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [changingDefault, setChangingDefault] = useState(false);

  // Fetch user's chatbots
  useEffect(() => {
    const fetchChatbots = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('chatbots')
          .select('id, public_id, name, is_default')
          .eq('user_id', user.id)
          .order('is_default', { ascending: false })
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        setChatbots(data || []);
      } catch (err) {
        console.error('Error fetching chatbots:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchChatbots();
  }, [user, chatbotPublicId]);

  // Get current chatbot
  const currentChatbot = chatbots.find(bot => bot.public_id === chatbotPublicId);

  // Set a chatbot as default
  const setAsDefault = async (id: string) => {
    if (!user) return;
    
    try {
      setChangingDefault(true);
      
      // First, set all chatbots to non-default
      await supabase
        .from('chatbots')
        .update({ is_default: false })
        .eq('user_id', user.id);
      
      // Then set the selected one as default
      await supabase
        .from('chatbots')
        .update({ is_default: true })
        .eq('id', id);
      
      // Refresh the list
      refetchChatbot();
      setIsOpen(false);
    } catch (err) {
      console.error('Error setting default chatbot:', err);
    } finally {
      setChangingDefault(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-lg text-gray-300">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading chatbots...</span>
      </div>
    );
  }

  if (chatbots.length <= 1) {
    return null; // Don't show selector if there's only one chatbot
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white/5 text-white rounded-lg hover:bg-white/10 transition-colors"
      >
        <span>{currentChatbot?.name || 'Select chatbot'}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          <div className="absolute z-50 mt-2 w-56 rounded-md shadow-lg bg-gray-800 border border-white/10 overflow-hidden">
            <div className="py-1">
              {chatbots.map((bot) => (
                <button
                  key={bot.id}
                  onClick={() => setAsDefault(bot.id)}
                  disabled={changingDefault}
                  className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-300 hover:bg-white/5 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    {bot.name}
                    {bot.is_default && (
                      <span className="text-xs bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded-full">
                        Default
                      </span>
                    )}
                  </span>
                  
                  {bot.public_id === chatbotPublicId && (
                    <Check className="w-4 h-4 text-indigo-400" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}